import { OktaAuthClient } from "../auth/okta";
import { ChatAppRequest } from "./types/models";
import {
  isCitationsEvent,
  isOtherLinksEvent,
  isContentDeltaEvent,
  isContentCompleteEvent,
  isFollowupQuestionsEvent,
  isResponseMetadataEvent,
  isErrorEvent,
  type APICitation,
  type FollowupQuestion
} from "./types/events";
import { parseNDJSONLine } from "./utils/parser";
import session from "../session";
import { ERROR_MESSAGES, ERROR_CODES } from "../constants";
import { API_HOST } from "./client";

export interface ResponseMetadata {
  id: string;
  selectedAgent: string;
  selectedAgentDisplayName?: string;
  recommendorResponse?: string;
  fallbackAgent?: string;
  isSystemFallback?: boolean;
  createdAt?: string;
}

export interface ErrorState {
  message: string;
  code: string;
  recoverable?: boolean;
}

export interface StreamState {
  content: string;
  citations: APICitation[];
  otherLinks: APICitation[];
  followupQuestions: FollowupQuestion[];
  metadata: ResponseMetadata | null;
  error: ErrorState | null;
  isComplete: boolean;
}

export type StreamCallback = (state: StreamState) => void;

let activeAbortController: AbortController | null = null;

export function cancelActiveRequest(): void {
  if (activeAbortController) {
    activeAbortController.abort();
    activeAbortController = null;
  }
}

interface Updater {
  flush: () => void;
}

function createUpdater(getState: () => StreamState, onStream: StreamCallback): Updater {
  const flush = () => {
    const state = getState();
    onStream({ ...state });
  };
  return { flush };
}

type EventAction = "flush" | "none";

function processEvent(event: unknown, state: StreamState): EventAction {
  if (isCitationsEvent(event)) {
    state.citations = event.citations;
    return "flush";
  }

  if (isOtherLinksEvent(event)) {
    state.otherLinks = event.links;
    return "flush";
  }

  if (isContentDeltaEvent(event)) {
    state.content += event.content;
    return "flush";
  }

  if (isContentCompleteEvent(event)) {
    state.content = event.content;
    return "flush";
  }

  if (isFollowupQuestionsEvent(event)) {
    state.followupQuestions = event.questions;
    return "flush";
  }

  if (isResponseMetadataEvent(event)) {
    state.metadata = {
      id: event.id,
      selectedAgent: event.selected_agent,
      selectedAgentDisplayName: event.selected_agent_display_name,
      recommendorResponse: event.recommendor_response,
      fallbackAgent: event.fallback_agent,
      isSystemFallback: event.is_system_fallback,
      createdAt: event.created_at
    };
    state.isComplete = true;
    return "flush";
  }

  if (isErrorEvent(event)) {
    state.error = {
      message: event.error,
      code: event.code,
      recoverable: event.recoverable
    };
    return "flush";
  }

  return "none";
}

function buildChatRequest(question: string, sessionId: string, history: ChatAppRequest["messages"] = [], systemPrompt?: string): ChatAppRequest {
  const req: ChatAppRequest = {
    sessionId,
    messages: [...history, { content: question, role: "user" }],
    stream: true,
    session_state: null
  };
  if (systemPrompt) {
    req.system_prompt = systemPrompt;
  }
  return req;
}

export async function streamChat(
  question: string,
  onStream: StreamCallback,
  history: ChatAppRequest["messages"] = [],
  systemPrompt?: string
): Promise<void> {
  cancelActiveRequest();

  const state: StreamState = {
    content: "",
    citations: [],
    otherLinks: [],
    followupQuestions: [],
    metadata: null,
    error: null,
    isComplete: false
  };

  const updater = createUpdater(() => state, onStream);

  activeAbortController = new AbortController();

  try {
    const isAuthenticated = await OktaAuthClient.isAuthenticated();
    if (!isAuthenticated) {
      throw new Error(ERROR_MESSAGES.NOT_AUTHENTICATED);
    }

    const sessionId = session.getId() ?? "";
    const request = buildChatRequest(question, sessionId, history, systemPrompt);

    const response = await fetch(API_HOST + "/chat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${OktaAuthClient.getAccessToken()}`
      },
      body: JSON.stringify(request),
      signal: activeAbortController.signal
    });

    if (!response.ok) {
      throw new Error(`${ERROR_MESSAGES.HTTP_ERROR} ${response.status}`);
    }

    if (!response.body) {
      throw new Error(ERROR_MESSAGES.NULL_RESPONSE_BODY);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      let readResult;
      try {
        readResult = await reader.read();
      } catch (readError) {
        if ((readError as Error).name === "AbortError") {
          return;
        }
        throw readError;
      }

      const { done, value } = readResult;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const event = parseNDJSONLine(line);
        if (!event) continue;

        const action = processEvent(event, state);

        if (action === "flush") {
          updater.flush();
        }
      }
    }
  } catch (e) {
    console.error("Error in streamChat:", e);
    activeAbortController?.abort();

    state.error = {
      message: e instanceof Error ? e.message : ERROR_MESSAGES.UNKNOWN_ERROR,
      code: ERROR_CODES.CLIENT_ERROR,
      recoverable: true
    };
    updater.flush();
  } finally {
    updater.flush();
  }
}
